// A looping illustration on the /web-uploader page: CSS keyframes on inline SVG, no libraries.
//
//  - Every animated element carries `data-a` and its own keyframes (generated by `kf` from a timeline), all
//    sharing one loop length, so the whole scene stays in step without JS timers.
//  - Paused while off-screen (IntersectionObserver) and by the visible Pause button (WCAG 2.2.2).
//  - prefers-reduced-motion: frozen on one meaningful frame (`freezeAt`), and the button is hidden.
//  - The scene itself is aria-hidden; the figure is described in text by its caption (`description`), so
//    nothing is shown only in the animation.
//  - Photo tiles (useFigureNear) only load once the figure comes near the viewport: until then (and in SSR)
//    they show their placeholder gradient.
import { useEffect, useRef, useState, type ReactNode } from "react";
import { Pause, Play } from "lucide-react";
import { FigureNear } from "./figureNear";
import { r } from "./keyframes";

const baseCss = (scope: string, durS: number, freezeAt: number) => `
.${scope} [data-a]{animation-duration:${durS}s;animation-iteration-count:infinite;animation-timing-function:linear;animation-fill-mode:both}
.${scope}[data-play="off"] [data-a]{animation-play-state:paused}
.${scope} [data-flip]{transform-box:fill-box;transform-origin:center}
@media (prefers-reduced-motion: reduce){.${scope} [data-a]{animation-delay:-${r((durS * freezeAt) / 100)}s!important;animation-play-state:paused!important}}
`;

interface Props {
  /** CSS class scoping this figure's rules (unique on the page). */
  scope: string;
  /** Loop length, seconds. */
  durationS: number;
  /** % of the loop shown, frozen, under prefers-reduced-motion. */
  freezeAt: number;
  /** The generated @keyframes. */
  keyframes: string;
  /** Accessible name of the illustration. */
  label: string;
  /** What the animation shows, in words (the visible caption). */
  description: ReactNode;
  pauseLabel: string;
  playLabel: string;
  /** The SVG scene (aria-hidden is applied here). */
  children: ReactNode;
  className?: string;
}

export const AnimatedFigure = ({
  scope, durationS, freezeAt, keyframes, label, description, pauseLabel, playLabel, children, className = "",
}: Props) => {
  const ref = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(true);
  const [paused, setPaused] = useState(false);
  const [near, setNear] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(([e]) => setVisible(e.isIntersecting), { threshold: 0.15 });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // Lazy photos: load them a little before the figure scrolls into view, once.
  useEffect(() => {
    const el = ref.current;
    if (near || !el) return;
    if (typeof IntersectionObserver === "undefined") { setNear(true); return; }
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) setNear(true); }, { rootMargin: "400px 0px" });
    io.observe(el);
    return () => io.disconnect();
  }, [near]);

  return (
    <figure
      ref={ref}
      className={`${scope} relative ${className}`}
      data-play={visible && !paused ? "on" : "off"}
      aria-label={label}
    >
      {/* Our own generated CSS (no user input); set as HTML so SSR doesn't escape the selectors' quotes. */}
      <style dangerouslySetInnerHTML={{ __html: baseCss(scope, durationS, freezeAt) + keyframes }} />
      <div className="relative">
        <div aria-hidden="true">
          <FigureNear.Provider value={near}>{children}</FigureNear.Provider>
        </div>
        <button
          type="button"
          onClick={() => setPaused((p) => !p)}
          aria-pressed={paused}
          className="absolute bottom-3 right-3 inline-flex h-8 w-8 items-center justify-center rounded-full border border-border bg-background/90 text-muted-foreground shadow-sm transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring motion-reduce:hidden"
          aria-label={paused ? playLabel : pauseLabel}
          title={paused ? playLabel : pauseLabel}
        >
          {paused ? <Play size={14} aria-hidden="true" /> : <Pause size={14} aria-hidden="true" />}
        </button>
      </div>
      <figcaption className="mt-4 text-sm text-muted-foreground">{description}</figcaption>
    </figure>
  );
};
